/**
 * RefineSearch mobile stack (filter drawer hub + category drill-down).
 *
 * Merged only into RefineSearchContainer. All API below uses the `rsm_` prefix
 * (refine search mobile) so it is obvious in the component and in grep.
 *
 * Data: rsm_mobileActiveFilter, rsm_mobileModelStage, rsm_mobileModelMakeKey, rsm_chevronRightIcon
 *
 * Computed: rsm_mobileHub*, rsm_mobileModelCheckbox*, rsm_activeMobileCategoryHasFilters
 *
 * Methods: rsm_openMobileFilter, rsm_resetLocalState, rsm_pushMobileStackSearchOptionsToParent, …
 *
 * External callers should use `rsm_*` on RefineSearchContainer refs. RefineSearchContainer may
 * expose `resetLocalState()` as a thin facade to `rsm_resetLocalState` for legacy call sites.
 */
import { evaChevronRight } from '@quasar/extras/eva-icons';
import refineSearchFormStateMixin from './refine-search-form-state-mixin';
import { REFINE_MOBILE_STACK_FILTER } from '../../utils/constants';
import { buildSearchParams } from '../../utils/refineSearchParamsBuilder';

export default {
  data() {
    return {
      /** @see utils/constants.js REFINE_MOBILE_STACK_FILTER — template v-if / @click ids */
      REFINE_MOBILE_STACK_FILTER,
      rsm_mobileActiveFilter: null,
      /** 'makeList' = pick a make; 'modelsForMake' = pick models for rsm_mobileModelMakeKey */
      rsm_mobileModelStage: 'makeList',
      rsm_mobileModelMakeKey: null,
      rsm_chevronRightIcon: evaChevronRight
    };
  },
  computed: {
    rsm_mobileHubShowMake() {
      return this.makes && this.makes.length > 1;
    },
    rsm_mobileHubShowPowerTrain() {
      return (
        (this.driveTrainOptions && this.driveTrainOptions.length > 0) || (this.tranmissionOptions && this.tranmissionOptions.length > 0)
      );
    },
    rsm_mobileHubHasSelectedMakes() {
      return Object.keys(this.selectedMakes || {}).some((make) => this.selectedMakes[make]);
    },
    rsm_mobileModelCheckboxOptions() {
      if (!this.rsm_mobileModelMakeKey) {
        return [];
      }
      const entry = this.filteredMakeModels.find((mm) => mm.make === this.rsm_mobileModelMakeKey);
      if (!entry || !entry.models) {
        return [];
      }
      return entry.models.map((m, index) => ({
        id: `${this.rsm_mobileModelMakeKey}-${index}`,
        label: this.formatWordsAsSentences(m),
        value: m
      }));
    },
    rsm_mobileModelCheckboxValue() {
      const selected = this.selectedModel[this.rsm_mobileModelMakeKey] || [];
      const map = {};
      this.rsm_mobileModelCheckboxOptions.forEach((opt) => {
        map[opt.value] = selected.includes(opt.value);
      });
      return map;
    },
    rsm_mobileHubMakeFilterCount() {
      const selected = this.filterResultsWithTrueValues(this.selectedMakes);
      return Array.isArray(selected) ? selected.length : 0;
    },
    rsm_mobileHubModelFilterCount() {
      return Object.values(this.selectedModel || {}).reduce((total, models) => {
        if (Array.isArray(models)) {
          return total + models.length;
        }
        if (typeof models === 'string' && models.length > 0) {
          return total + 1;
        }
        return total;
      }, 0);
    },
    /** Matches refineSearchParamsBuilder: price range when min set; weekly when min != null */
    rsm_mobileHubBudgetFilterCount() {
      let n = 0;
      if (this.selectedPrice?.min) {
        n += 1;
      }
      if (this.selectedWeeklyBudget?.min != null) {
        n += 1;
      }
      return n;
    },
    rsm_mobileHubFuelFilterCount() {
      const selected = this.filterResultsWithTrueValues(this.selectedFuelType);
      return Array.isArray(selected) ? selected.length : 0;
    },
    rsm_mobileHubPowerTrainFilterCount() {
      const drive = this.filterResultsWithTrueValues(this.selectedPowerTrain);
      const trans = this.filterResultsWithTrueValues(this.selectedTranmission);
      return (Array.isArray(drive) ? drive.length : 0) + (Array.isArray(trans) ? trans.length : 0);
    },
    rsm_mobileHubStyleFilterCount() {
      const sumLen = (obj) => {
        const keys = this.filterResultsWithTrueValues(obj);
        return Array.isArray(keys) ? keys.length : 0;
      };
      return (
        sumLen(this.selectedBodyType) + sumLen(this.selectedCabType) + sumLen(this.selectedVehicleCategory) + sumLen(this.selectedSeat)
      );
    },
    /** Mobile drawer stack: whether the open category screen has any selections (for showing Reset). */
    rsm_activeMobileCategoryHasFilters() {
      if (!this.mobileStackNavigation || !this.hideSubmitButton || !this.rsm_mobileActiveFilter) {
        return false;
      }
      const id = this.rsm_mobileActiveFilter;
      const F = REFINE_MOBILE_STACK_FILTER;
      if (id === F.MAKE) {
        return this.rsm_mobileHubMakeFilterCount > 0;
      }
      if (id === F.MODEL) {
        if (this.rsm_mobileModelStage === 'modelsForMake' && this.rsm_mobileModelMakeKey) {
          return this.rsm_selectedModelCountForMake(this.rsm_mobileModelMakeKey) > 0;
        }
        return this.rsm_mobileHubModelFilterCount > 0;
      }
      if (id === F.BUDGET) {
        return this.rsm_mobileHubBudgetFilterCount > 0;
      }
      if (id === F.FUEL) {
        return this.rsm_mobileHubFuelFilterCount > 0;
      }
      if (id === F.POWER_TRAIN) {
        return this.rsm_mobileHubPowerTrainFilterCount > 0;
      }
      if (id === F.STYLE) {
        return this.rsm_mobileHubStyleFilterCount > 0;
      }
      return false;
    }
  },
  watch: {
    makes: {
      handler() {
        this.rsm_applySingleMakeSelection();
      },
      deep: true
    },
    rsm_activeMobileCategoryHasFilters: {
      handler(val) {
        if (this.mobileStackNavigation && this.hideSubmitButton) {
          this.$emit('active-mobile-category-has-filters', val);
        }
      },
      immediate: true
    }
  },
  methods: {
    rsm_openMobileFilter(id) {
      if (id === REFINE_MOBILE_STACK_FILTER.MODEL && !this.rsm_mobileHubHasSelectedMakes) {
        return;
      }
      this.rsm_mobileModelStage = 'makeList';
      this.rsm_mobileModelMakeKey = null;
      this.rsm_mobileActiveFilter = id;
      this.rsm_emitMobileStackDetailState();
    },
    rsm_openMobileModelForMake(makeKey) {
      this.rsm_mobileModelMakeKey = makeKey;
      this.rsm_mobileModelStage = 'modelsForMake';
    },
    /** Count of models selected for one make (same rules as rsm_mobileHubModelFilterCount). */
    rsm_selectedModelCountForMake(make) {
      const models = this.selectedModel?.[make];
      if (Array.isArray(models)) {
        return models.length;
      }
      if (typeof models === 'string' && models.length > 0) {
        return 1;
      }
      return 0;
    },
    /**
     * Mobile drawer stack: pop one level (models-for-make → make list, or detail → hub).
     * Called from parent controls (e.g. drawer header Back).
     */
    rsm_mobileRefineBack() {
      if (this.rsm_mobileActiveFilter === REFINE_MOBILE_STACK_FILTER.MODEL && this.rsm_mobileModelStage === 'modelsForMake') {
        this.rsm_mobileModelStage = 'makeList';
        this.rsm_mobileModelMakeKey = null;
        return;
      }
      this.rsm_closeMobileFilterStack();
    },
    rsm_onMobileModelCheckboxInput(selectedMap) {
      const models = Object.keys(selectedMap).filter((k) => selectedMap[k]);
      if (!this.rsm_mobileModelMakeKey) {
        return;
      }
      if (models.length === 0) {
        this.$delete(this.selectedModel, this.rsm_mobileModelMakeKey);
      } else {
        this.$set(this.selectedModel, this.rsm_mobileModelMakeKey, models);
      }
      this.rsm_handleModelUpdated();
    },
    /**
     * Mobile drawer only: return to the filter hub and emit `mobile-stack-detail` false.
     * Call when closing the drawer or resetting navigation so the next open starts at the hub.
     */
    rsm_closeMobileFilterStack() {
      this.rsm_mobileActiveFilter = null;
      this.rsm_mobileModelStage = 'makeList';
      this.rsm_mobileModelMakeKey = null;
      this.rsm_emitMobileStackDetailState();
    },
    /** True when a category drill-down panel is open (mobile stack navigation only). */
    rsm_emitMobileStackDetailState() {
      if (!this.mobileStackNavigation) {
        return;
      }
      this.$emit('mobile-stack-detail', this.rsm_mobileActiveFilter != null);
    },
    /**
     * Clears mobile stack UI state and delegates to shared refine form reset mixin.
     * Safe for parent `ref` usage when discarding draft filters or after successful submit flows.
     */
    rsm_resetLocalState() {
      this.rsm_mobileActiveFilter = null;
      this.rsm_mobileModelStage = 'makeList';
      this.rsm_mobileModelMakeKey = null;
      refineSearchFormStateMixin.methods.resetLocalState.call(this);
      this.rsm_emitMobileStackDetailState();
    },
    /**
     * Mobile drawer stack only: clear the active category (Make, Model, …) without touching other
     * refine fields, sort, repayment, or fetching. Parent searchOptions sync via rsm_handleFilterChange.
     */
    rsm_resetActiveMobileCategoryFilters() {
      if (!this.mobileStackNavigation || !this.hideSubmitButton || !this.rsm_mobileActiveFilter) {
        return;
      }
      const id = this.rsm_mobileActiveFilter;
      const F = REFINE_MOBILE_STACK_FILTER;
      switch (id) {
        case F.MAKE: {
          const cleared = {};
          const list =
            this.calculatorManufacturers && this.calculatorManufacturers.length > 0
              ? this.calculatorManufacturers
              : (this.makes || []).map((m) => m.value);
          list.forEach((raw) => {
            cleared[String(raw).toUpperCase()] = false;
          });
          this.selectedMakes = cleared;
          this.selectedModel = {};
          break;
        }
        case F.MODEL:
          if (this.rsm_mobileModelStage === 'modelsForMake' && this.rsm_mobileModelMakeKey) {
            this.$delete(this.selectedModel, this.rsm_mobileModelMakeKey);
            this.rsm_mobileModelStage = 'makeList';
            this.rsm_mobileModelMakeKey = null;
          } else {
            this.selectedModel = {};
          }
          break;
        case F.BUDGET:
          this.selectedPrice = { min: null, max: 100000 };
          this.selectedWeeklyBudget = { min: null, max: 200 };
          break;
        case F.FUEL:
          this.selectedFuelType = {};
          break;
        case F.POWER_TRAIN:
          this.selectedPowerTrain = {};
          this.selectedTranmission = {};
          break;
        case F.STYLE:
          this.selectedBodyType = {};
          this.selectedCabType = {};
          this.selectedVehicleCategory = {};
          this.selectedSeat = {};
          break;
        default:
          break;
      }
      this.rsm_applySingleMakeSelection();
      this.rsm_handleFilterChange();
    },
    rsm_applySingleMakeSelection() {
      if (this.makes?.length === 1) {
        this.selectedMakes = { [this.makes[0].value]: true };
      }
    },
    rsm_handleFilterChange() {
      this.enableSubmitButton();
      this.rsm_pushMobileStackSearchOptionsToParent();
    },
    rsm_handleModelUpdated() {
      this.enableSubmitButton();
      this.rsm_pushMobileStackSearchOptionsToParent();
    },
    /**
     * Mobile filter drawer: QDialog unmounts this component when closed, so `value` (searchOptions)
     * must stay in sync with form state as the user edits—otherwise budgetType / budget amounts
     * reset when they go back to the hub or reopen the drawer (getInitialOptions only runs on mount).
     */
    rsm_pushMobileStackSearchOptionsToParent() {
      if (!this.mobileStackNavigation || !this.hideSubmitButton || this._isInitializing) {
        return;
      }
      const formState = {
        selectedMakes: this.selectedMakes,
        selectedModel: this.selectedModel,
        selectedFuelType: this.selectedFuelType,
        selectedBodyType: this.selectedBodyType,
        selectedCabType: this.selectedCabType,
        selectedVehicleCategory: this.selectedVehicleCategory,
        selectedSeat: this.selectedSeat,
        selectedPowerTrain: this.selectedPowerTrain,
        selectedTranmission: this.selectedTranmission,
        selectedWeeklyBudget: this.selectedWeeklyBudget,
        selectedPrice: this.selectedPrice,
        budgetType: this.budgetType
      };
      const params = buildSearchParams(formState, { value: this.value });
      const merged = { ...this.value, ...params };
      const keysConditionalFromBuild = [
        'pair',
        'vehicleFueltype',
        'vehicleBodytype',
        'cabType',
        'vehicleCategory',
        'vehicleSeats',
        'vehicleMinRRP',
        'vehicleMaxRRP',
        'vehicleDriveTrain',
        'vehicleTransmission',
        'minBudget',
        'maxBudget',
        'budgetType'
      ];
      keysConditionalFromBuild.forEach((key) => {
        if (!(key in params)) {
          delete merged[key];
        }
      });
      this.$emit('input', merged);
    }
  }
};
